import Vue from 'vue';
import { i18n } from '@/plugins/i18n';
import conversationsService from '../services/conversationsService';
import { PAGE_SIZE } from '../constants/messagingConstants';

// Dedupe concurrent fetchNextPage calls for the SAME list state only.
// Keyed by listVersion so a folder switch/refresh always starts a fresh request
// instead of returning the previous folder's (soon-to-be-discarded) in-flight promise.
let inflightFetch = null;
let inflightVersion = -1;

const state = {
  conversations: [],
  folder: 'inbox',
  page: 0,
  hasMore: true,
  isLoading: false,
  loadedOnce: false,
  paneOpen: false,
  selectMode: false,
  activeConversationUid: null,
  counts: {},
  sources: [],
  sourcesLoaded: false,
  selectedUids: [],
  // bumped whenever the list resets, so infinite-scroll components can re-arm
  listVersion: 0,
};

const getters = {
  conversations: (state) => state.conversations,
  folder: (state) => state.folder,
  counts: (state) => state.counts,
  sources: (state) => state.sources,
  sourcesLoaded: (state) => state.sourcesLoaded,
  isLoading: (state) => state.isLoading,
  loadedOnce: (state) => state.loadedOnce,
  paneOpen: (state) => state.paneOpen,
  selectMode: (state) => state.selectMode,
  activeConversationUid: (state) => state.activeConversationUid,
  hasMore: (state) => state.hasMore,
  listVersion: (state) => state.listVersion,
  selectedUids: (state) => state.selectedUids,
  selectedConversations: (state) => state.conversations
    .filter((conversation) => state.selectedUids.includes(conversation.uid)),
  isSelected: (state) => (uid) => state.selectedUids.includes(uid),
};

const mutations = {
  resetList(state, folder) {
    state.conversations = [];
    state.page = 0;
    state.hasMore = true;
    state.loadedOnce = false;
    state.isLoading = false;
    state.folder = folder || state.folder;
    state.selectedUids = [];
    state.activeConversationUid = null;
    state.listVersion += 1;
  },
  setPaneOpen(state, value) {
    state.paneOpen = value;
  },
  setSelectMode(state, value) {
    state.selectMode = value;
  },
  setActiveConversationUid(state, uid) {
    state.activeConversationUid = uid;
  },
  appendConversations(state, { conversations, page }) {
    state.conversations = [...state.conversations, ...conversations];
    state.page = page;
    state.hasMore = conversations.length >= PAGE_SIZE;
    state.loadedOnce = true;
  },
  replaceConversation(state, conversation) {
    const index = state.conversations.findIndex((item) => item.uid === conversation.uid);
    if (index !== -1) Vue.set(state.conversations, index, conversation);
  },
  setLoading(state, value) {
    state.isLoading = value;
  },
  setCounts(state, counts) {
    state.counts = counts || {};
  },
  setSources(state, sources) {
    state.sources = sources || [];
    state.sourcesLoaded = true;
  },
  setSelectedUids(state, uids) {
    state.selectedUids = uids;
  },
};

const actions = {
  // The list itself is fetched by the infinite-scroll component, which re-arms when
  // listVersion changes — a single fetch path avoids double-fetch races.
  async setFolder({ commit, dispatch }, folder) {
    commit('resetList', folder);
    await dispatch('fetchCounts');
  },
  async fetchNextPage({
    state, commit, dispatch, rootGetters,
  }) {
    if (!state.hasMore) return [];
    const version = state.listVersion;
    // only dedupe against an in-flight request for the current list state
    if (inflightFetch && inflightVersion === version) return inflightFetch;
    inflightVersion = version;
    commit('setLoading', true);
    inflightFetch = (async () => {
      try {
        const staffData = rootGetters['StaffStore/staffData'];
        const teamsViewEnabled = rootGetters['StaffStore/staffTeamsViewEnabled'];
        const teamsViewFilter = teamsViewEnabled
          ? rootGetters['StaffStore/staffTeamsViewState'] : null;
        const page = state.page + 1;
        const conversations = await conversationsService.fetchConversations({
          folder: state.folder,
          page,
          perPage: PAGE_SIZE,
          teamsViewFilter: teamsViewFilter || staffData?.teams_view_filter,
        });
        // a folder switch/refresh happened while this request was in flight — drop it
        if (state.listVersion !== version) return [];
        commit('appendConversations', { conversations: conversations || [], page });
        return conversations || [];
      } catch (error) {
        console.error('[MessagingStore] Error fetching conversations:', error);
        if (state.listVersion === version) {
          commit('appendConversations', { conversations: [], page: state.page + 1 });
          dispatch('ToastStore/setToastConfig', {
            value: true,
            type: 'attention',
            bodyText: i18n.t('messaging.errors.list_load_failed'),
          }, { root: true });
        }
        return [];
      } finally {
        // only the current-version fetch clears shared state / toggles loading —
        // a stale request resolving must not clobber a newer folder's fetch
        if (state.listVersion === version) {
          inflightFetch = null;
          inflightVersion = -1;
          commit('setLoading', false);
        }
      }
    })();
    return inflightFetch;
  },
  async refresh({ state, dispatch, commit }) {
    commit('resetList', state.folder);
    await dispatch('fetchCounts');
    dispatch('ConversationStore/fetchConversationCounts', null, { root: true });
  },
  async setPaneOpen({ commit }, value) {
    commit('setPaneOpen', value);
    try {
      await conversationsService.setInboxSidePane(value);
    } catch (error) {
      console.error('[MessagingStore] Error persisting pane preference:', error);
    }
  },
  async fetchCounts({ commit }) {
    try {
      const counts = await conversationsService.fetchCounts();
      commit('setCounts', counts);
    } catch (error) {
      console.error('[MessagingStore] Error fetching counts:', error);
    }
  },
  async fetchSources({ state, commit }, { force = false } = {}) {
    if (state.sourcesLoaded && !force) return;
    try {
      const sources = await conversationsService.fetchSources();
      commit('setSources', sources);
    } catch (error) {
      console.error('[MessagingStore] Error fetching sources:', error);
    }
  },
  toggleSelect({ state, commit }, uid) {
    const selected = state.selectedUids.includes(uid)
      ? state.selectedUids.filter((selectedUid) => selectedUid !== uid)
      : [...state.selectedUids, uid];
    commit('setSelectedUids', selected);
  },
  selectAll({ state, commit }) {
    commit('setSelectedUids', state.conversations.map((conversation) => conversation.uid));
  },
  selectNone({ commit }) {
    commit('setSelectedUids', []);
  },
  toggleSelectMode({ state, commit }) {
    const next = !state.selectMode;
    commit('setSelectMode', next);
    if (!next) commit('setSelectedUids', []);
  },
  // flags: e.g. { business_unread: false } / { archived: true } / { deleted: true } / { spam: true }
  async updateSelected({ getters, dispatch }, flags) {
    const selected = getters.selectedConversations;
    await Promise.all(selected.map(
      (conversation) => conversationsService.updateConversation(conversation.uid, flags),
    ));
    await dispatch('refresh');
    return selected.length;
  },
  async updateSingle({ commit }, { uid, flags }) {
    const updated = await conversationsService.updateConversation(uid, flags);
    if (updated?.uid) commit('replaceConversation', updated);
    return updated;
  },
  async assignSelected({ getters, dispatch }, { staffId, notes }) {
    const selected = getters.selectedConversations;
    await Promise.all(selected.map(
      (conversation) => conversationsService.assignConversation(conversation.uid, staffId, notes),
    ));
    await dispatch('refresh');
    return selected.length;
  },
  async sendLeadSelected({ getters, dispatch }, { staffIds, notes }) {
    const selected = getters.selectedConversations;
    await Promise.all(selected.map(
      (conversation) => conversationsService.sendLead(conversation.uid, staffIds, notes),
    ));
    await dispatch('refresh');
    return selected.length;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
